import React, { useMemo, useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';
import {
  MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX,
} from '../constants/muiNativeTableDenseLayout';
import { useGlobalDenseMode } from '../hooks/useGlobalDenseMode';
import { endpoints, joinUrl } from '../api/config';
import { useCachedData } from '../hooks/useCachedData';
import { Box, Paper, Typography, TextField, InputAdornment, CircularProgress, Tooltip, IconButton } from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon, SaveAlt as SaveAltIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';

const deepFlatten = (obj, prefix = '') => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    const key = prefix ? `${prefix}${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.assign(out, deepFlatten(v, `${key}_`));
    } else {
      out[key] = v;
    }
  });
  return out;
};

const Errors = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Column hide removed; always show all columns

  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();

  const { data: rawData, isLoading, error, refetch } = useCachedData(endpoints.errorsStatus, {
    refetchInterval: 30000,
    staleTime: 20000,
  });

  const data = useMemo(() => {
    if (!rawData) return [];
    if (Array.isArray(rawData)) return rawData.map(r => (r && typeof r === 'object' ? deepFlatten(r) : r));
    if (typeof rawData === 'object') {
      // If object-of-arrays or object-of-objects, flatten values
      const rows = [];
      Object.values(rawData).forEach((v) => {
        if (Array.isArray(v)) {
          v.forEach((item) => rows.push(deepFlatten(item)));
        } else if (v && typeof v === 'object') {
          rows.push(deepFlatten(v));
        }
      });
      return rows.length ? rows : [deepFlatten(rawData)];
    }
    return [];
  }, [rawData]);

  // no column visibility menu

  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try { await refetch(); } finally { setIsRefreshing(false); }
  };

  const [retryLoading, setRetryLoading] = useState(false);
  const [rowRetryingId, setRowRetryingId] = useState(null);
  const actionTextSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: 'primary.main',
    cursor: 'pointer',
    userSelect: 'none',
    textDecoration: 'none',
    textUnderlineOffset: '2px',
    '&:hover': { color: 'primary.dark', textDecoration: 'underline' },
  };
  const handleRetryAll = useCallback(async () => {
    if (retryLoading) return;
    setRetryLoading(true);
    try {
      await api.get(endpoints.errorsRetryAll);
      await refetch();
    } catch (e) {
      console.error('Retry all failed:', e);
    } finally {
      setRetryLoading(false);
    }
  }, [retryLoading, refetch]);

  const handleRetryOne = useCallback(async (row) => {
    if (!row || rowRetryingId) return;
    const id = row.id || row.error_id || row._id || row.record_id;
    if (!id) return;
    setRowRetryingId(id);
    try {
      await api.get(endpoints.errorsRetryOne, {
        params: { id }
      });
      await refetch();
    } catch (e) {
      console.error('Retry one failed:', e);
    } finally {
      setRowRetryingId(null);
    }
  }, [rowRetryingId, refetch]);

  const allKeys = useMemo(() => (Array.isArray(data) && data.length > 0 ? Array.from(new Set(data.flatMap(row => Object.keys(row || {})))) : []), [data]);
  const visibleKeys = allKeys;
  const RETRY_MARKER = '__retry__';
  const keysWithRetry = useMemo(() => {
    const keys = [...visibleKeys];
    const matchIndex = (candidates) => keys.findIndex(k => candidates.includes(String(k).toLowerCase()));
    const idIdx = matchIndex(['id', 'error_id', '_id', 'record_id']);
    const binDataIdx = matchIndex(['bin_data', 'bindata']);
    const binIdIdx = matchIndex(['bin_id', 'binid']);

    let insertAt = 0;
    if (idIdx !== -1) {
      // Prefer between id and bin_data/bin_id
      if (binDataIdx !== -1 && binDataIdx > idIdx) insertAt = binDataIdx;
      else if (binIdIdx !== -1 && binIdIdx > idIdx) insertAt = binIdIdx;
      else insertAt = idIdx + 1;
    } else if (binDataIdx !== -1) {
      insertAt = binDataIdx; // before bin_data
    } else if (binIdIdx !== -1) {
      insertAt = binIdIdx; // before bin_id
    } else {
      insertAt = 0;
    }

    keys.splice(insertAt, 0, RETRY_MARKER);
    return keys;
  }, [visibleKeys]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const filteredRows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    const lower = debouncedSearch.toLowerCase();
    // Hardcoded inclusive lower bound date in UTC (YYYY-MM-DD)
    const todayISO = '2025-09-16';

    const getCreatedDateISO = (row) => {
      // Priority keys for created date
      const candidateKeys = Object.keys(row || {});
      const preferred = candidateKeys.find(k => k.toLowerCase() === 'created_at' || k.toLowerCase() === 'created_date');
      const fallback = candidateKeys.find(k => k.toLowerCase().includes('created') || k.toLowerCase().endsWith('_at'));
      const key = preferred || fallback;
      if (!key) return null;
      const val = row[key];
      if (!val) return null;
      try {
        // If it's already YYYY-MM-DD
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        const d = new Date(val);
        if (isNaN(d.getTime())) return null;
        // Normalize to UTC date-only string
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      } catch {
        return null;
      }
    };

    return data.filter((row) => {
      // Search filter
      const matchesSearch = lower ? allKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(lower)) : true;
      // Date filter: created date on or after today
      const createdISO = getCreatedDateISO(row);
      const matchesDate = createdISO ? (createdISO >= todayISO) : true;
      return matchesSearch && matchesDate;
    });
  }, [data, debouncedSearch, allKeys]);

  const gridColumns = useMemo(
    () =>
      keysWithRetry.map((k) => {
        if (k === RETRY_MARKER) {
          return {
            field: RETRY_MARKER,
            headerName: 'Action',
            sortable: false,
            filterable: false,
            flex: 0.5,
            minWidth: 100,
            renderCell: (params) => {
              const row = params.row;
              const rowId = row.id || row.error_id || row._id || row.record_id;
              const isRowRetrying = rowRetryingId === rowId;
              return (
                <Box
                  component="span"
                  role="button"
                  tabIndex={isRowRetrying ? -1 : 0}
                  onClick={() => handleRetryOne(row)}
                  onKeyDown={(e) => {
                    if (isRowRetrying) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRetryOne(row);
                    }
                  }}
                  sx={{
                    ...actionTextSx,
                    opacity: isRowRetrying ? 0.6 : 1,
                    pointerEvents: isRowRetrying ? 'none' : 'auto',
                  }}
                >
                  {isRowRetrying ? (
                    <>
                      <CircularProgress size={14} sx={{ mr: 0.5 }} /> Retrying
                    </>
                  ) : (
                    'Retry'
                  )}
                </Box>
              );
            },
          };
        }
        return {
          field: k,
          headerName: k.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          resizable: true,
          flex: 1,
          minWidth: 150,
          renderCell: (params) => {
            const val = params.row?.[k];
            if (val === null || val === undefined || val === '') return '-';
            if (typeof val === 'object') return JSON.stringify(val);
            return String(val);
          },
        };
      }),
    [keysWithRetry, rowRetryingId, handleRetryOne]
  );
  const gridRows = useMemo(
    () =>
      filteredRows.map((row, idx) => ({
        ...row,
        id: row.id ?? row.error_id ?? row._id ?? row.record_id ?? `error-${idx}`,
      })),
    [filteredRows]
  );

  const handleDownloadCSV = () => {
    if (!filteredRows.length || visibleKeys.length === 0) return;
    const header = visibleKeys.join(',');
    const process = (val) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object') return JSON.stringify(val).replace(/"/g, '""');
      return String(val).replace(/"/g, '""');
    };
    const rows = filteredRows.map((row) => visibleKeys.map((k) => `"${process(row[k])}"`).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'netsuite_errors.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1.5, fontSize: '1.25rem' }}>
          NetSuite Errors
        </Typography>

        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}>
            <TextField
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{ 
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                sx: { fontSize: '0.875rem' }
              }}
              fullWidth
            />
          </Box>
          <Box sx={{ ...MUI_NATIVE_TABLE_TOOLBAR_ACTIONS_SX, gap: 0.5 }}>
            <Tooltip title="Refresh data">
              <IconButton onClick={handleRefreshClick} disabled={isRefreshing} size="small" aria-label="Refresh data" sx={{ padding: '6px' }}>
                {isRefreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <GlobalDenseModeToolbar
              denseMode={denseMode}
              denseModeTogglePending={denseModeTogglePending}
              persistDenseMode={persistDenseMode}
            />
            <Box
              component="span"
              role="button"
              tabIndex={retryLoading ? -1 : 0}
              onClick={retryLoading ? undefined : handleRetryAll}
              onKeyDown={(e) => {
                if (retryLoading) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRetryAll();
                }
              }}
              sx={{
                ...actionTextSx,
                opacity: retryLoading ? 0.6 : 1,
                pointerEvents: retryLoading ? 'none' : 'auto',
              }}
            >
              {retryLoading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : <ReplayIcon sx={{ mr: 0.5, fontSize: '1rem' }} />}
              Retry All
            </Box>
            <Tooltip title="Download CSV">
              <IconButton onClick={handleDownloadCSV} size="small" aria-label="Download CSV" sx={{ padding: '6px' }}>
                <SaveAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Paper elevation={2} sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {error ? <Box sx={{ p: 1, color: 'error.main', fontSize: '0.8125rem' }}>{String(error?.message || error)}</Box> : null}
          <HoverExpandDataGridPro
            rows={gridRows}
            columns={gridColumns}
            loading={isLoading}
            disableVirtualization
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            pagination
            paginationMode="client"
            pageSizeOptions={[5, 10, 25, 50, 100, 1000, { value: -1, label: 'All' }]}
            initialState={{ pagination: { paginationModel: { pageSize: 1000, page: 0 } } }}
            rowHeight={denseMode ? 36 : 52}
            columnHeaderHeight={denseMode ? 40 : 56}
            sx={{ flex: 1, minHeight: 0 }}
          />
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default Errors;


